import React, { useState, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo, breadcrumbJsonLd, faqJsonLd, articleJsonLd, itemListJsonLd } from "../lib/seo";
import { Section, SectionHead, Breadcrumb, Button, Badge, Spinner } from "../components/ui";
import { Markdown } from "../components/markdown";
import { FaqAccordion } from "../components/marketing";
import { useReveal } from "../lib/useReveal";

const SEG = { resource: "resources", tool: "tools", ranking: "best", alternative: "alternatives" };
const COPY = {
  resource: ["Resources", "Free resources for teams selling in the DMs.", "Playbooks, swipe files, worked models and diagnostics. No signup. No PDF gates."],
  tool: ["Free tools", "Free calculators for teams selling in the DMs.", "Real inputs, live results in pesos, formulas disclosed. Never gated."],
  ranking: ["Rankings", "Best AI tools, honestly ranked in 2026.", "Scored on the same rubric — including our own product — against real 2026 pricing."],
  alternative: ["Alternatives", "Why teams switch to BloomCONNECT.", "Honest comparisons: what each competitor is good at, and where it falls short for DM selling."],
};
const FACET = { resource: "resource_type", tool: "tool_category", ranking: "ranking_group" };

export function ContentIndex({ type }) {
  const [sp] = useSearchParams();
  const { data } = useQuery({ queryKey: ["content", type], queryFn: () => get("/content", { type }) });
  const facetKey = FACET[type];
  const items = data || [];
  const facets = useMemo(() => facetKey ? [...new Set(items.map((i) => i[facetKey]).filter(Boolean))] : [], [items, facetKey]);
  const initial = sp.get("type") ? sp.get("type").replace(/s$/, "").replace("swipe-file", "swipe-file").replace("playbook", "playbook").replace("diagnostic", "diagnostic") : "";
  const [active, setActive] = useState(initial);
  const filtered = active ? items.filter((i) => i[facetKey] === active) : items;
  useReveal(filtered);
  const [eyebrow, title, intro] = COPY[type];

  return (
    <>
      <Seo title={eyebrow} path={`/${SEG[type]}/`} description={intro} jsonLd={itemListJsonLd(items.map((i) => i.title))} />
      <Section>
        <SectionHead label={eyebrow} title={title} intro={intro} />
        {facets.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            <button onClick={() => setActive("")} className={`badge ${!active ? "!bg-ink-950 !text-white !border-ink-950" : ""}`}>All</button>
            {facets.map((f) => <button key={f} onClick={() => setActive(f)} className={`badge ${active === f ? "!bg-ink-950 !text-white !border-ink-950" : ""}`}>{f}</button>)}
          </div>
        )}
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <Link key={p.slug} to={`/${SEG[type]}/${p.slug}/`} className="card-hairline p-6 hover:border-brand-600 reveal-on-scroll flex flex-col">
              <div className="flex items-center justify-between"><span className="eyebrow">{p[facetKey] || p.category}</span>{p.featured && <Badge>Featured</Badge>}</div>
              <h3 className="mt-3 text-lg text-ink-950">{p.title}</h3>
              <p className="mt-2 text-sm text-ink-600 flex-1">{p.description}</p>
              <div className="mt-4 font-mono text-2xs text-ink-400">{p.reading_time} min read</div>
            </Link>
          ))}
        </div>
        {filtered.length === 0 && <p className="mt-8 text-ink-400">Nothing here yet.</p>}
      </Section>
    </>
  );
}

export function ContentDetail({ type }) {
  const { slug } = useParams();
  const { data: p, isLoading } = useQuery({ queryKey: [type, slug], queryFn: () => get(`/content/${type}/${slug}`) });
  useReveal(p);
  if (isLoading) return <Spinner />;
  if (!p) return <Section><p className="text-ink-400">Not found.</p></Section>;
  const [eyebrow] = COPY[type];
  const jsonLd = [breadcrumbJsonLd([{ name: eyebrow, path: `/${SEG[type]}/` }, { name: p.title, path: `/${SEG[type]}/${slug}/` }]), articleJsonLd(p), faqJsonLd(p.faq)];
  if (type === "ranking") jsonLd.push(itemListJsonLd([p.title]));
  return (
    <>
      <Seo title={p.title} path={`/${SEG[type]}/${slug}/`} description={p.description} jsonLd={jsonLd} />
      <Section>
        <Breadcrumb items={[{ name: eyebrow, path: `/${SEG[type]}/` }, { name: p.title }]} />
        <div className="mt-6 grid gap-10 lg:grid-cols-[1fr,300px]">
          <article>
            {(p.last_verified || p.author) && <div className="font-mono text-2xs text-ink-400 mb-4">{p.author && <span>By {p.author}. </span>}Last verified: {p.last_verified || p.updated}</div>}
            <Markdown source={p.body} />
            {p.faq?.length > 0 && <div className="mt-12"><h2 className="text-2xl text-ink-950 mb-4">FAQ</h2><FaqAccordion items={p.faq} /></div>}
          </article>
          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="card-hairline p-5">
              <div className="eyebrow mb-2">Ready to try it?</div>
              <p className="text-sm text-ink-600 mb-4">Put an AI sales agent in your inbox in about fifteen minutes.</p>
              <Button to="/pricing/" variant="primary" size="md" className="w-full" data-testid="content-see-pricing-btn">See Pricing</Button>
            </div>
            {p.related?.length > 0 && <div><div className="eyebrow mb-3">Related</div><ul className="space-y-2">{p.related.map((r) => <li key={r.slug}><Link to={`/${SEG[r.type] || SEG[type]}/${r.slug}/`} className="text-sm text-ink-600 hover:text-brand-700">{r.title}</Link></li>)}</ul></div>}
          </aside>
        </div>
      </Section>
    </>
  );
}
